import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';

export const RELOAD_FLAG_KEY = 'errorFallback:chunkReload';

const CHUNK_LOAD_MESSAGE_PATTERN = /Loading chunk \S+ failed/i;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError' || CHUNK_LOAD_MESSAGE_PATTERN.test(error.message);
}

// Atomic check-and-set for the chunk-reload guard. Returns true only if we just
// claimed a fresh reload. If sessionStorage throws (disabled, quota, locked-down
// browser), we treat the claim as failed so we never auto-reload without a working
// loop guard — otherwise a broken deploy would loop forever for those users.
function tryClaimChunkReload(): boolean {
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG_KEY) !== null) return false;
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

function handleReload(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    // Swallow so an explicit user reload still proceeds.
  }
  window.location.reload();
}

function handleGoHome(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    // Swallow so navigation still proceeds.
  }
  // Hard navigation, not router push: the boundary may be mounted because the
  // router or a provider above it failed, so client-side navigation can't be
  // trusted to recover.
  window.location.assign('/');
}

interface ErrorFallbackViewProps {
  error: unknown;
}

function ErrorFallbackView({ error }: ErrorFallbackViewProps) {
  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    if (!tryClaimChunkReload()) return;
    window.location.reload();
  }, [error]);

  return (
    <Box
      role="alert"
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 6,
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480 }}>
        An unexpected error occurred and this page can&apos;t be displayed. Try reloading, or go
        back to the home page if the problem persists.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button variant="contained" color="primary" onClick={handleReload}>
          Reload page
        </Button>
        <Button variant="outlined" color="primary" onClick={handleGoHome}>
          Go to home
        </Button>
      </Box>
    </Box>
  );
}

export interface SentryFallbackProps {
  error: unknown;
  componentStack?: string;
  eventId?: string;
  resetError: () => void;
}

export function ErrorFallback({ error }: SentryFallbackProps): JSX.Element {
  return <ErrorFallbackView error={error} />;
}

export function RouteErrorElement(): JSX.Element {
  const error = useRouteError();
  return <ErrorFallbackView error={error} />;
}
